// noinspection HtmlRequiredAltAttribute

/*
 * Copyright (c) New Cloud Technologies, Ltd., 2013-2020
 *
 * You can not use the contents of the file in any way without New Cloud Technologies, Ltd. written permission.
 * To obtain such a permit, you should contact New Cloud Technologies, Ltd. at http://ncloudtech.com/contact.html
 *
 */

import React from 'react';
import AmrApi from './amrApi';
import { EDITOR_UI_LANGUAGES, AMR_SOURCE } from './constants';
import {
    getFileNameFromSearch,
    getFileByAJAX,
    getReadOnlyFromSearch,
    getLanguageCodeFromSearch,
} from './utils';
import './styles.css';
import { MobilePage } from './MobilePage';

export class App extends React.Component {
    state = {
        editorLoaded: false,
        hasChanges: false,
        documentOpening: false,
        documentOpened: false,
        lang: Object.keys(EDITOR_UI_LANGUAGES)[0],
        isReadonly: false,
        mainBlockShown: true,
        popupShow: false,
    };

    inputRef = React.createRef();
    name = '';

    componentDidMount() {
        if (this._iframe) {
            const pathWithDomain = /^https?:\/\//.test(AMR_SOURCE);

            let origin;
            if (pathWithDomain) {
                const url = new URL(AMR_SOURCE);
                origin = url.protocol + '//' + url.host;
            } else {
                origin = window.location.origin;
            }

            this._amrApi = new AmrApi(this._iframe, origin);

            this._amrApi.onChange(({ hasChanges }) => {
                this.setState({ hasChanges });
            });

            this._amrApi.onPageReloadRequested(() => window.location.reload());

            this._amrApi.onError(this.onError);
        } else {
            console.warn('Something went wrong, no iframe ref');
        }

        window.addEventListener('beforeunload', (event) => {
            if (!this.state.hasChanges) {
                return;
            }
            event.preventDefault();
            const UNSAVED_CHANGES_TEXT =
                'Вы уверены?\nВнесенные в документ изменения не сохранятся.';
            return (event.returnValue = UNSAVED_CHANGES_TEXT);
        });
    }

    handleToggleLanguageByUrl() {
        const language = getLanguageCodeFromSearch(window.location.search);

        if (language && EDITOR_UI_LANGUAGES[language]) {
            this.setState({
                lang: language,
            });
        }
    }

    handleToggleReadOnlyModeByUrl() {
        const readonly = getReadOnlyFromSearch(window.location.search);

        if (readonly) {
            this.setState({
                isReadonly: readonly,
            });
        }
    }

    handleDownloadFileByUrl() {
        let fileNameFromSearch = getFileNameFromSearch(window.location.search);
        if (fileNameFromSearch === '1') fileNameFromSearch = 'new.docx';
        if (fileNameFromSearch === '2') fileNameFromSearch = 'new.xlsx';
        if (fileNameFromSearch === '3') fileNameFromSearch = 'sample.docx';
        if (fileNameFromSearch === '4') fileNameFromSearch = 'sample.xlsx';
        if (fileNameFromSearch) {
            getFileByAJAX(fileNameFromSearch).then((arrayBuffer) => {
                if (arrayBuffer) {
                    this.setState({
                        documentOpening: true,
                        mainBlockShown: false,
                    });
                    this.openDocumentByURL(fileNameFromSearch, arrayBuffer);
                }
            });
        }
    }

    requestToOpenFileByName = (filename) => {
        const fileNameFix = getFileNameFromSearch('?document=' + filename);
        if (fileNameFix) {
            getFileByAJAX(fileNameFix).then((arrayBuffer) => {
                if (arrayBuffer) {
                    this.setState({
                        documentOpening: true,
                        mainBlockShown: false,
                    });
                    this.openDocumentByURL(fileNameFix, arrayBuffer);
                }
            });
        }
    };

    static _getFileName(file) {
        return file.includes('/')
            ? file.substring(file.lastIndexOf('/') + 1)
            : file;
    }

    requestToOpenByFileInput = () => {
        if (!this.inputRef.current) {
            return;
        }

        this.inputRef.current.click();
    };

    requestToSave = (format, extension = false) => {
        this._amrApi
            .saveDocument(format, extension)
            .then((content) => {
                const blob = new Blob([content]);
                const link = document.createElement('a');
                // Browsers that support HTML5 download attribute
                if (link.download !== undefined) {
                    let name = this.name;
                    const currentExt = this.name.substr(
                        name.lastIndexOf('.') + 1
                    );
                    let ext;
                    if (extension) {
                        ext = extension;
                    } else {
                        ext = AmrApi.getDocumentExtension(
                            this.documentType,
                            format,
                            currentExt
                        );
                    }
                    const url = URL.createObjectURL(blob);

                    if (ext && currentExt !== ext) {
                        name = `${name.substr(
                            0,
                            name.lastIndexOf('.')
                        )}.${ext}`;
                    }

                    link.setAttribute('href', url);
                    link.setAttribute('download', name);
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(url));
                }
            })
            .catch(this.onError);
    };

    openDocumentByFileInput = () => {
        const file = this.inputRef.current.files[0];
        if (file) {
            this.setState({ documentOpening: true, documentOpened: false });
            document
                .getElementsByClassName('editor')[0]
                .contentWindow.location.reload();
            setTimeout(() => {
                this.setState({ documentOpening: true, mainBlockShown: false });

                const fileName = App._getFileName(file.name);
                this.name = fileName;

                const fileReader = new FileReader();

                fileReader.onerror = this.onError;
                fileReader.onload = (event) => {
                    this.openDocument(fileName, event.target.result);
                };

                fileReader.readAsArrayBuffer(file);
            }, 500);
        }
    };

    openDocumentByURL = (fileName, arrayBuffer) => {
        this.name = fileName;

        this.openDocument(fileName, arrayBuffer);
    };

    openDocument = (fileName, arrayBuffer) => {
        this._amrApi
            .openDocument({
                mode: 'default',
                content: arrayBuffer,
                filename: fileName,
                lang: this.state.lang,
                usePerfHelper: false,

            })
            .then(({ type }) => {
                this.documentType = type;
                this.setState({
                    documentOpened: true,
                    documentOpening: false,
                    mainBlockShown: false,
                });
            })
            .catch(this.onError);
    };

    onError = (reason) => {
        this.setState({ error: reason, documentOpening: false });
        //eslint-disable-next-line no-console
        console.log('Editor error: ', reason);
    };

    onLoadIFrame = () => {
        this.setState({ editorLoaded: true }, () => {
            this.handleDownloadFileByUrl();
            this.handleToggleReadOnlyModeByUrl();
            this.handleToggleLanguageByUrl();
        });
    };

    setEditor = (ref) => {
        if (ref) {
            ref.addEventListener('load', this.onLoadIFrame);
        }

        this._iframe = ref;
    };

    readOnlyChanged = (event) => {
        this.setState({
            isReadonly: event.target.checked,
        });
    };

    _onSelectLanguage = (event) => {
        this.setState({ lang: event.target.value });
    };

    reloadOpenFile(f) {
        this.setState({ documentOpening: true, documentOpened: false });
        document
            .getElementsByClassName('editor')[0]
            .contentWindow.location.reload();
        setTimeout(() => this.requestToOpenFileByName(f), 500);
    }

    checkChanges(t) {
        if (this.state.hasChanges) {
            this.setState({ popupShow: true });
            let tmp = document.getElementById('open-button');
            tmp.setAttribute('data-link', t);
        } else {
            if (t === 'file') {
                let but = document.getElementById('open-button');
                but.click();
            } else {
                this.reloadOpenFile(t);
            }
        }
    }

    okPopup() {
        this.setState({ hasChanges: false });
        this.setState({ popupShow: false });
        let plink = document.getElementById('open-button');
        let t = plink.getAttribute('data-link');
        if (t === 'file') {
            plink.click();
        } else {
            this.reloadOpenFile(t);
        }
    }

    closePopup() {
        this.setState({ popupShow: false });
    }

    renderHeader() {
        const {
            documentOpened,
            editorLoaded,
            popupShow,
            documentOpening,
            error,
        } = this.state;
        return (
            <div className="header">
                {popupShow && (
                    <>
                        <div className="confirm-bg" />
                        <div className="confirm-popup">
                            <div className="confirm-popup-text">
                                Вы уверены?
                                <br />
                                Внесенные в документ изменения не сохранятся.
                            </div>
                            <a
                                onClick={() => {
                                    this.okPopup();
                                }}
                                href="#"
                                id="confirm_popup_ok"
                            >
                                Ок
                            </a>
                            <a
                                onClick={() => {
                                    this.closePopup();
                                }}
                                href="#"
                                id="confirm_popup_cancel"
                            >
                                Отмена
                            </a>
                        </div>
                    </>
                )}
                <div className="header-img-outer">
                    <a href={''} id="amr_editor_home">
                        <img src="img/logo.png" className="header-img" />
                        <span className="header-title">
                            МойОфис Веб-редакторы. Пробная версия
                        </span>
                        {/*<div className="img-version">{version}</div>*/}
                    </a>
                </div>
                {!error && !editorLoaded && <span>Загрузка...</span>}
                {!error && documentOpening && (
                    <span>Документ загружается...</span>
                )}

                <div>
                    {!documentOpened && (
                        <>
                            {documentOpening && (
                                <>
                                    <div className="dropdown-link dropdown-link-inactive">
                                        <img
                                            src="img/icon-create.png"
                                            className="dropdown-icon dropdown-icon-save"
                                        />
                                        Создать
                                    </div>
                                    <div className="dropdown-link dropdown-link-inactive">
                                        <img
                                            src="img/icon-open.png"
                                            className="dropdown-icon dropdown-icon-save"
                                        />
                                        Открыть
                                    </div>
                                </>
                            )}
                            {!documentOpening && (
                                <>
                                    <div className="dropdown-link">
                                        <img
                                            src="img/icon-create.png"
                                            className="dropdown-icon dropdown-icon-create"
                                        />
                                        Создать
                                        <div className="dropdown-block">
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'new.docx'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_create_text"
                                            >
                                                <img
                                                    src="img/icon-text.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-txt"
                                                />
                                                Текстовый документ
                                            </a>
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'new.xlsx'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_create_table"
                                            >
                                                <img
                                                    src="img/icon-table.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-table"
                                                />
                                                Табличный документ
                                            </a>
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'new.odp'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_create_presentation"
                                            >
                                                <img
                                                    src="img/icon-presentation.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-table"
                                                />
                                                Презентацию
                                            </a>
                                        </div>
                                    </div>
                                    <div className="dropdown-link">
                                        <img
                                            src="img/icon-open.png"
                                            className="dropdown-icon dropdown-icon-open"
                                        />
                                        Открыть
                                        <div className="dropdown-block">
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'sample.docx'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_open_text"
                                            >
                                                <img
                                                    src="img/icon-text.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-txt"
                                                />
                                                Пример текстового документа
                                            </a>
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'sample.xlsx'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_open_table"
                                            >
                                                <img
                                                    src="img/icon-table.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-table"
                                                />
                                                Пример табличного документа
                                            </a>
                                            <a
                                                onClick={() => {
                                                    this.requestToOpenFileByName(
                                                        'example.pptx'
                                                    );
                                                }}
                                                href="#"
                                                id="amr_editor_open_presentation"
                                            >
                                                <img
                                                    src="img/icon-presentation.svg"
                                                    className="dropdown-block-icon dropdown-block-icon-table"
                                                />
                                                Пример презентации
                                            </a>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="dropdown-link dropdown-link-inactive">
                                <img
                                    src="img/icon-save.png"
                                    className="dropdown-icon dropdown-icon-save"
                                />
                                Сохранить
                            </div>
                            <div className="dropdown-link dropdown-link-inactive">
                                <img
                                    src="img/icon-q.svg"
                                    className="dropdown-icon dropdown-icon-q"
                                />
                            </div>
                        </>
                    )}
                    {documentOpened && (
                        <>
                            <div className="dropdown-link">
                                <img
                                    src="img/icon-create.png"
                                    className="dropdown-icon dropdown-icon-create"
                                />
                                Создать
                                <div className="dropdown-block">
                                    <div className="dropdown-block-title">
                                        Создать
                                    </div>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('new.docx');
                                        }}
                                        href="#"
                                        id="check_amr_editor_create_text"
                                    >
                                        <img
                                            src="img/icon-text.svg"
                                            className="dropdown-block-icon dropdown-block-icon-txt"
                                        />
                                        Текстовый документ
                                    </a>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('new.xlsx');
                                        }}
                                        href="#"
                                        id="check_amr_editor_create_table"
                                    >
                                        <img
                                            src="img/icon-table.svg"
                                            className="dropdown-block-icon dropdown-block-icon-table"
                                        />
                                        Табличный документ
                                    </a>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('new.odp');
                                        }}
                                        href="#"
                                        id="check_amr_editor_create_presentation"
                                    >
                                        <img
                                            src="img/icon-presentation.svg"
                                            className="dropdown-block-icon dropdown-block-icon-table"
                                        />
                                        Презентацию
                                    </a>
                                    {/*<a className="hidden-v" onClick={() => {this.reloadOpenFile('new.docx')}} href="#" id="amr_editor_create_text"><img src="img/icon-txt.png" className="dropdown-block-icon dropdown-block-icon-txt" />Текстовый документ</a>
                                    <a className="hidden-v" onClick={() => {this.reloadOpenFile('new.xlsx')}} href="#" id="amr_editor_create_table"><img src="img/icon-table.png" className="dropdown-block-icon dropdown-block-icon-table" />Табличный документ</a>*/}
                                </div>
                            </div>
                            <div className="dropdown-link">
                                <img
                                    src="img/icon-open.png"
                                    className="dropdown-icon dropdown-icon-open"
                                />
                                Открыть
                                <div className="dropdown-block">
                                    <div className="dropdown-block-title">
                                        Открыть
                                    </div>
                                    {/*<a href="?"><img src="img/icon-file.png" className="dropdown-block-icon dropdown-block-icon-file" />Выбрать файл</a>*/}
                                    <button
                                        id="open-button"
                                        onClick={this.requestToOpenByFileInput}
                                    >
                                        Open
                                    </button>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('file');
                                        }}
                                        href="#"
                                        className="open-file"
                                        id="open-file"
                                    >
                                        <img
                                            src="img/icon-file.png"
                                            className="dropdown-block-icon dropdown-block-icon-file"
                                        />
                                        Выбрать файл...
                                    </a>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('sample.docx');
                                        }}
                                        href="#"
                                        id="amr_editor_open_text"
                                    >
                                        <img
                                            src="img/icon-text.svg"
                                            className="dropdown-block-icon dropdown-block-icon-txt"
                                        />
                                        Пример текстового документа
                                    </a>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('sample.xlsx');
                                        }}
                                        href="#"
                                        id="amr_editor_open_table"
                                    >
                                        <img
                                            src="img/icon-table.svg"
                                            className="dropdown-block-icon dropdown-block-icon-table"
                                        />
                                        Пример табличного документа
                                    </a>
                                    <a
                                        onClick={() => {
                                            this.checkChanges('example.pptx');
                                        }}
                                        href="#"
                                        id="amr_editor_open_presentation"
                                    >
                                        <img
                                            src="img/icon-presentation.svg"
                                            className="dropdown-block-icon dropdown-block-icon-table"
                                        />
                                        Пример презентации
                                    </a>
                                </div>
                            </div>
                            <div className="dropdown-link">
                                <img
                                    src="img/icon-save.png"
                                    className="dropdown-icon dropdown-icon-save"
                                />
                                Сохранить
                                <div className="dropdown-block dropdown-block-save">
                                    <div className="dropdown-block-title">
                                        Сохранить
                                    </div>
                                    {this.documentType === 'document' && (
                                        <>
                                            <button
                                                id="amr_save_text_oxml"
                                                key="OXML"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave('OXML')
                                                }
                                            >
                                                <img
                                                    src="img/icon-docx.png"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'docx'}</strong>
                                                {' Документ Word'}
                                            </button>
                                            <button
                                                id="amr_save_text_odf"
                                                key="ODF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave(
                                                        'ODF',
                                                        'odt'
                                                    )
                                                }
                                            >
                                                <img
                                                    src="img/icon-odt.png"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'odt'}</strong>
                                                {' Документ OpenDocument'}
                                            </button>
                                            <button
                                                id="amr_save_text_pdf"
                                                key="PDF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave(
                                                        'PDF',
                                                        'pdf'
                                                    )
                                                }
                                            >
                                                <img
                                                    src="img/icon-pdf.svg"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'pdf'}</strong>
                                                {' Документ PDF'}
                                            </button>
                                        </>
                                    )}
                                    {this.documentType === 'spreadsheet' && (
                                        <>
                                            <button
                                                id="amr_save_spreadsheet_oxml"
                                                key="OXML"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave('OXML')
                                                }
                                            >
                                                <img
                                                    src="img/icon-xlsx.png"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'xlsx'}</strong>
                                                {' Книга Excel'}
                                            </button>
                                            <button
                                                id="amr_save_spreadsheet_odf"
                                                key="ODF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave(
                                                        'ODF',
                                                        'ods'
                                                    )
                                                }
                                            >
                                                <img
                                                    src="img/icon-ods.png"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'ods'}</strong>
                                                {' Таблица OpenDocument'}
                                            </button>
                                            <button
                                                id="amr_save_spreadsheet_pdf"
                                                key="PDF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave(
                                                        'PDF',
                                                        'pdf'
                                                    )
                                                }
                                            >
                                                <img
                                                    src="img/icon-pdf.svg"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'pdf'}</strong>
                                                {' Документ PDF'}
                                            </button>
                                        </>
                                    )}
                                    {this.documentType === 'presentation' && (
                                        <>
                                            <button
                                                id="amr_save_presentation_oxml"
                                                key="OXML"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave('OXML','pptx')
                                                }
                                            >
                                                <img
                                                    src="img/icon-pptx.png"
                                                    className="dropdown-icon-format dropdown-icon-save-presentation"
                                                />
                                                <strong>{'pptx'}</strong>
                                                {' Презентация PowerPoint'}
                                            </button>
                                            <button
                                                id="amr_save_presentation_odf"
                                                key="ODF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave('ODF','odp')
                                                }
                                            >
                                                <img
                                                    src="img/icon-odp.png"
                                                    className="dropdown-icon-format dropdown-icon-save-presentation"
                                                />
                                                <strong>{'odp'}</strong>
                                                {' Презентация OpenDocument'}
                                            </button>
                                            <button
                                                id="amr_save_presentation_pdf"
                                                key="PDF"
                                                className="save-button"
                                                onClick={() =>
                                                    this.requestToSave(
                                                        'PDF',
                                                        'pdf'
                                                    )
                                                }
                                            >
                                                <img
                                                    src="img/icon-pdf.svg"
                                                    className="dropdown-icon-format"
                                                />
                                                <strong>{'pdf'}</strong>
                                                {' Документ PDF'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="dropdown-link">
                                <img
                                    src="img/icon-q.svg"
                                    className="dropdown-icon dropdown-icon-q"
                                />
                                <div className="dropdown-block dropdown-block-save">
                                    <a
                                        href="https://myoffice.ru/policy/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                                        target="_blank"
                                        rel="noreferrer"
                                        id="amr_editor_policy"
                                    >
                                        <img
                                            src="img/icon-policy.svg"
                                            className="dropdown-icon-format"
                                        />
                                        Политика конфиденциальности
                                    </a>
                                    <a
                                        href="https://myoffice.ru/tos/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                                        target="_blank"
                                        rel="noreferrer"
                                        id="amr_editor_license"
                                    >
                                        <img
                                            src="img/icon-legal.svg"
                                            className="dropdown-icon-format"
                                        />
                                        Условия использования
                                    </a>
                                    <a
                                        href="https://myoffice.ru/trial/?feedback&utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                                        target="_blank"
                                        rel="noreferrer"
                                        id="amr_editor_feedback"
                                    >
                                        <img
                                            src="img/icon-feedback.svg"
                                            className="dropdown-icon-format"
                                        />
                                        Обратная связь
                                    </a>
                                </div>
                            </div>
                            <div className="dropdown-mobile">
                                <span />
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    renderMainBlock() {
        const { mainBlockShown } = this.state;
        return (
            <>
                <div className={`main-block ${mainBlockShown ? '' : 'hidden'}`}>
                    <a
                        href="https://myoffice.ru/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                        target="_blank"
                        rel="noreferrer"
                        className="main-block-logo"
                        id="amr_main_home"
                    >
                        <img src="img/logo-ru.svg" />
                    </a>
                    <div className="main-block-title">
                        Воспользуйтесь пробной версией веб-редакторов
                        <br />
                        МойОфис прямо сейчас
                    </div>
                    <a
                        onClick={() => {
                            this.requestToOpenFileByName('new.docx');
                        }}
                        href="#"
                        id="amr_main_create_text"
                        className="main-block-button main-block-button-text"
                    >
                        <img src="img/icon-text.svg" />
                        {'Создать текстовый документ'}
                    </a>
                    <a
                        onClick={() => {
                            this.requestToOpenFileByName('new.xlsx');
                        }}
                        href="#"
                        id="amr_main_create_table"
                        className="main-block-button main-block-button-table"
                    >
                        <img src="img/icon-table.svg" />
                        {'Создать табличный документ'}
                    </a>
                    <a
                        onClick={() => {
                            this.requestToOpenFileByName('new.odp');
                        }}
                        href="#"
                        id="amr_main_create_presentation"
                        className="main-block-button main-block-button-presentation"
                    >
                        <img src="img/icon-presentation.svg" />
                        {'Создать презентацию'}
                    </a>
                    <a
                        href="#"
                        onClick={() => {
                            this.requestToOpenByFileInput();
                        }}
                        id="amr_main_open_file"
                        className="main-block-button main-block-button-open"
                    >
                        <img src="img/icon-open-file.svg" />
                        {'Открыть документ'}
                    </a>
                    <div className="main-block-info">
                        Открытие документов DOCX, ODT, TXT, XLSX, ODS, CSV, PPTX, ODP или PDF
                    </div>
                    <a
                        href="https://support.myoffice.ru/products/myoffice-sdk/#system_requirements"
                        id="amr_main_requirements"
                        className="main-block-system"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Системные требования
                    </a>
                </div>
                <div
                    className={`main-block-footer ${
                        mainBlockShown ? '' : 'hidden'
                    }`}
                >
                    <div className="main-block-footer-inner">
                        <div className="main-block-footer-left">
                            Разработана на базе Автономного модуля
                            редактирования.
                            <br />
                            Узнайте про все возможности <br />
                            <a
                                href="https://myoffice.ru/products/sdk/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                                target="_blank"
                                rel="noreferrer"
                                id="amr_main_sdk"
                            >
                                «МойОфис Комплект Средств Разработки (SDK)»
                            </a>
                            .
                        </div>
                        <a
                            href="https://myoffice.ru/policy/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                            target="_blank"
                            rel="noreferrer"
                            id="amr_main_policy"
                        >
                            Политика конфиденциальности
                        </a>
                        <br />
                        <a
                            href="https://myoffice.ru/tos/?utm_source=edit.myoffice.ru&utm_medium=referral&utm_campaign=amr"
                            target="_blank"
                            id="amr_main_tos"
                            rel="noreferrer"
                        >
                            Условия использования
                        </a>
                        <div>
                            © ООО «Новые Облачные Технологии», 2013–
                            {new Date().getFullYear()}. Все права защищены.
                        </div>
                    </div>
                </div>
                <div
                    className={`editor-container ${
                        mainBlockShown ? 'hidden' : ''
                    }`}
                >
                    <iframe
                        title={AMR_SOURCE}
                        className="editor"
                        src={AMR_SOURCE}
                        ref={this.setEditor}
                    />
                </div>
            </>
        );
    }

    render() {
        return (
            <>
                <input
                    type="file"
                    style={{ display: 'none' }}
                    accept={AmrApi.getOptions().SUPPORTED_MIME_TYPES.join(',')}
                    onChange={this.openDocumentByFileInput}
                    ref={this.inputRef}
                />
                {this.state.mainBlockShown ? '' : this.renderHeader()}
                {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent
                ) ? (
                    <MobilePage />
                ) : (
                    this.renderMainBlock()
                )}
            </>
        );
    }
}
